import React from 'react';
import type { KpiViewMode } from './line-item-daily-kpis';
import { KpiCard, KpiValueSkeleton, KpiViewToggle } from './daily-kpis-shared';
import type { KpiMetric } from './pacing-daily-kpis';
import { DeliveryVarianceHighlight, SeverityHighlightValue } from './delivery-variance-highlight';

export type Last7DaysDailyKpisLayoutProps = {
  viewMode: KpiViewMode;
  setViewMode: (mode: KpiViewMode) => void;
  loading?: boolean;
  unitAbbr: string;
  yesterday: KpiMetric;
  avg3Day: string;
  avg7Day: string;
  variance: { actual: number; planned: number; formattedDifference: string };
};

export function Last7DaysDailyKpisLayout({
  viewMode,
  setViewMode,
  loading,
  unitAbbr,
  yesterday,
  avg3Day,
  avg7Day,
  variance
}: Last7DaysDailyKpisLayoutProps) {
  const isSpend = viewMode === 'spend';
  const valueSuffix = isSpend ? undefined : unitAbbr;
  const varianceLabel = isSpend ? '7 day spend' : unitAbbr ? `7 day ${unitAbbr}` : '7 day units';

  return (
    <div className="border-y border-[#3c4f6a] bg-gradient-to-r from-transparent via-[rgba(60,79,106,0.1)] to-transparent py-6">
      <div className="flex items-center">
        <div className="shrink-0 pr-6">
          <KpiViewToggle viewMode={viewMode} setViewMode={setViewMode} />
        </div>
        <KpiCard
          label="Yesterday"
          value={
            loading ? (
              <KpiValueSkeleton />
            ) : !isSpend && yesterday.actual != null && yesterday.planned != null ? (
              <SeverityHighlightValue
                value={yesterday.formatted}
                actual={yesterday.actual}
                planned={yesterday.planned}
                suffix={valueSuffix}
              />
            ) : (
              <span className="inline-flex items-center gap-1">
                {yesterday.formatted}
                {valueSuffix ? (
                  <span className="text-xs font-normal text-gray-600">{valueSuffix}</span>
                ) : null}
              </span>
            )
          }
        />
        <KpiCard
          label="3 day avg."
          value={
            loading ? (
              <KpiValueSkeleton />
            ) : (
              <span className="inline-flex items-center gap-1">
                {avg3Day}
                <span className="text-xs font-normal text-gray-600">/day</span>
              </span>
            )
          }
        />
        <KpiCard
          label="7 day avg."
          value={
            loading ? (
              <KpiValueSkeleton />
            ) : (
              <span className="inline-flex items-center gap-1">
                {avg7Day}
                <span className="text-xs font-normal text-gray-600">/day</span>
              </span>
            )
          }
        />
        <KpiCard
          label={varianceLabel}
          value={
            loading ? (
              <KpiValueSkeleton />
            ) : isSpend ? (
              <span className="text-lg font-semibold text-[#181d1f]">
                {variance.formattedDifference}
              </span>
            ) : (
              <DeliveryVarianceHighlight
                actual={variance.actual}
                planned={variance.planned}
                formattedDifference={variance.formattedDifference}
              />
            )
          }
        />
      </div>
    </div>
  );
}
